import { styled } from '@mui/material/styles';
import { Affinity, SustainableFinanceDisclosureRegulationClobDto } from 'models/dtoTypes';
import Stack from '@mui/material/Stack';
import SubSection from './_subSection';
import SdgScoreClob, { ISdgData } from './_sdgScoreClob';
import { taxonomySvg } from 'kosmos/images';
import EsgLegand from './_esgLegand';
import Empty from './_empty';
import { smartGovernancePrimaryColor } from 'kosmosConfig';

const PREFIX = 'SustainableFinanceDisclosureRegulationReport';

const classes = {
  item: `${PREFIX}-item`,
};

const Root = styled('div')(() => ({
  [`& .${classes.item}`]: {
    width: '100%',
    padding: 0,
  },
}));

interface IProps {
  name?: string;
  sustainableFinanceDisclosureRegulationReport: SustainableFinanceDisclosureRegulationClobDto[];
  sustainableFinanceDisclosureRegulationReportSmartGovernance?: SustainableFinanceDisclosureRegulationClobDto[];
}

export default function SustainableFinanceDisclosureRegulationReport(props: IProps): JSX.Element {
  const {
    name = 'Activity check against Sustainable Finance Disclosure Regulation (SFDR) & Principle Adverse Impact (PAI) score, mapped using positive and negative SDGs. ',
    sustainableFinanceDisclosureRegulationReport,
    sustainableFinanceDisclosureRegulationReportSmartGovernance,
  } = props;

  const renderSingleItem = (item: SustainableFinanceDisclosureRegulationClobDto, index: number) => {
    const secondaryItem = (sustainableFinanceDisclosureRegulationReportSmartGovernance || []).find(i => i.number == item.number);
    const secondaryAffinity = secondaryItem?.affinity ?? Affinity.Unscored;

    return (
      <div key={index} className={classes.item}>
        <SdgScoreClob
          weight="regular"
          key={index}
          withBg={true}
          title={item.title || ''}
          affinity={item.affinity}
          secondaryAffinity={secondaryAffinity}
        />
      </div>
    );
  };

  const renderHeader = () => {
    const style = {
      display: 'inline-block',
      width: 50,
      marginRight: 10,
    };
    return (
      <div>
        <div style={{...style, textAlign: 'center'}}>Auto</div>
        <div style={{...style, color: smartGovernancePrimaryColor, textAlign: 'center'}}>SG</div>
      </div>
    );
  };

  const renderContent = () => {
    if (sustainableFinanceDisclosureRegulationReport.length === 0) {
      return <Empty title="There is no data available"/>;
    }

    return (
      <Root>
        {renderHeader()}
        <Stack direction="column" spacing={2} >
          {sustainableFinanceDisclosureRegulationReport.map(renderSingleItem)}
        </Stack>
      </Root>
    );
  };

  const renderLegands = () => {
    const legands: ISdgData[] = [
      {
        affinity: 'negative',
        title: 'Negative activity found towards corresponding SFDR.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'warning',
        title: 'Signs of both positive and negative activity found towards corresponding SFDR, but mostly negative.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'positive',
        title: 'Positive activity found towards corresponding SFDR.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'neutral',
        title: 'Signs of balanced positive and negative activity found towards corresponding SFDR.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'unscored',
        title: 'Not applicable to this company, or not enough information available.',
        withBg: true,
        size: 'small',
      },
    ];

    return (
      <EsgLegand
        legands={legands}
        title={name}
        learnMoreTabId="sfdr"
      />
    );
  };

  return (
    <SubSection
      title="Sustainable Finance Disclosure Regulation"
      isBold
      icon={<img src={taxonomySvg} />}
    >
      <>
        {renderLegands()}
        {renderContent()}
      </>
    </SubSection>
  );
}

